import React, { Suspense, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch, useLocation } from "react-router-dom";
import { CContainer, CFade } from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import CompanyList from "./CompanyList";
import { startLogout } from "../redux/actions/auth";
import ErrorBoundary from "../components/ErrorBoundary";

// routes config
import routes from "../routes";
import NoAccess from "./NoAccess";
import { CompanyContext, UserContext } from "src/context";
import { FULL_ACCESS_PLANS, PLANS_SMARTCFO, Plans } from "src/utils/plans";
import { ACCOUNTING_ROUTE_PATHS, isAccountingEnabledByEnv } from "src/utils/accounting";
import { Roles } from "src/utils/roles";
import { useModulePermissions } from "src/hooks/useModulePermissions";
import { useHeapModuleTracking } from "src/hooks/useHeapModuleTracking";
import DefaultLandingRedirect from "src/components/routing/DefaultLandingRedirect";
import ReportModuleLoadingSkeleton from "src/components/shared/ReportModuleLoadingSkeleton";
import F29ModuleLoadingSkeleton from "src/components/f29/F29ModuleLoadingSkeleton";

const PAGE_TITLE_I18N_KEYS = {
  "/dashboard": "pageTitle",
};

const FINANCE_PAGE_TITLE_KEYS = {
  "/flujo_caja": "cashflow",
  "/eerr": "eerr",
  "/eoaf": "eoaf",
  "/creditos": "loans",
  "/conciliacion": "reconciliation",
};

const DOCUMENT_PAGE_TITLE_KEYS = {
  "/documentos_tributarios": "dtes",
  "/boletas": "boletas",
  "/obligaciones": "obligaciones",
  "/cuotas_de_creditos": "creditInstallments",
  "/declaraciones_de_ingresos": "din",
};

const SIDEBAR_PAGE_TITLE_KEYS = {
  "/reportes_ventas": "reporteVentas",
  "/reportes_compras": "reporteCompras",
  "/reportes_cobranza": "reporteCobranza",
  "/reportes_proveedores": "reporteProveedores",
  "/reportes_rrhh": "reporteRRHH",
  "/reportes": "reportes",
  "/reportes_dj": "reporteDJs",
  "/cobranza": "cobranza",
  "/formulario-29": "formulario29",
  "/consolidado-f29": "consolidadoF29Hub",
  "/multas-sii": "multasDelSii",
  "/asientos": "asientos",
  "/balance_8_columnas": "balance8Columnas",
  "/balance_clasificado": "balanceClasificado",
  "/estado_de_resultados_contable": "estadoResultadosContable",
  "/libro_mayor": "libroMayor",
  "/libro_diario": "libroDiario",
  "/plan_de_cuentas": "planDeCuentas",
  "/estructuras_maestras": "estructurasMaestras",
  "/estado_de_resultados_gestion": "estadoResultadosGestion",
  "/balance_clasificado_gestion": "balanceClasificadoGestion",
  "/centralizacion_contable": "centralizacionContable",
  "/libro-compra-venta": "auditor",
  "/cierre_apertura_contable": "cierreAperturaContable",
  "/avance_contable": "avanceContable",
  "/softlanding": "softlanding",
  "/repositorio": "repository",
  "/ficha_cliente": "fichaCliente",
  "/conexiones": "conexiones",
  "/agenda": "agenda",
  "/descargas": "descargas",
  "/cuadre_cartolas": "cuadreCartolas",
  "/configuracion": "configuracion",
  "/configuracion2": "configuracion",
  "/haberes_variables": "haberesVariables",
  "/validador_liquidaciones": "validadorLiquidaciones",
  "/cargar_nuevo_empleado": "cargarNuevoEmpleado",
  "/cargar_nuevo_empleado/invitar/:token": "cargarNuevoEmpleado",
  "/cargar_nuevo_empleado/borrador/:draftId": "cargarNuevoEmpleado",
  "/contratos_proximos_a_vencer": "contratosProximosAVencer",
};

const REPORT_MODULE_PATHS = new Set([
  "/reportes_ventas",
  "/reportes_compras",
  "/reportes_cobranza",
  "/reportes_proveedores",
  "/reportes_rrhh",
  "/reportes",
  "/reportes_dj",
  "/reportes_personalizados",
  "/cobranza",
]);

function useCheckSession(sessionExp) {
  const logOut = localStorage.getItem("logOut");
  let location = useLocation();
  const dispatch = useDispatch();
  React.useEffect(() => {
    const now = new Date().getTime() / 1000;
    if (now > sessionExp || logOut == "true") {
      localStorage.setItem("logOut", false);
      dispatch(startLogout());
    }
  }, [location, logOut]);
}
const Content = (props) => {
  const {
    companyName,
    showCompanyList,
    setShowCompanyList,
    hideCompanyList,
    setupdateSideBar,
    isFixed,
    isMobile
  } = props

  const user = useContext(UserContext)
  const { sessionExp, roles } = useSelector((state) => state.auth)
  const { selectedCompany } = useSelector((state) => state.companyList)
  const companyPlans = selectedCompany?.plan || []
  const { hasRouteAccess } = useModulePermissions();
  const { t } = useTranslation("dashboard");
  const { t: tSidebar } = useTranslation("sidebar");
  const { t: tFinance } = useTranslation("finance");
  const { t: tDocuments } = useTranslation("documents");
  const location = useLocation();

  useHeapModuleTracking();

  const suspenseFallback = useMemo(
    () =>
      location.pathname === "/formulario-29" || location.pathname === "/estado_dj" ? (
        <F29ModuleLoadingSkeleton />
      ) : REPORT_MODULE_PATHS.has(location.pathname) ? (
        <ReportModuleLoadingSkeleton />
      ) : (
        <div className="pt-3 text-center">
          <div className="sk-spinner sk-spinner-pulse"></div>
        </div>
      ),
    [location.pathname]
  );
  const loading = suspenseFallback;

  useCheckSession(sessionExp)
  // "/" is handled only by DefaultLandingRedirect (never a real page).
  const Routes = routes.filter((f) => f.path !== '/').map(f => {
    // PRIMERO: Verificar permisos del admin panel
    // Si el usuario no tiene acceso a esta ruta, mostrar NoAccess
    if (f.path && !hasRouteAccess(f.path)) {
      return { ...f, ...{ component: NoAccess } }
    }

    // Cobranza y Conciliación: acceso controlado únicamente desde el panel admin
    // (modulePermissions / hasRouteAccess), ya no por roles del usuario.

    // Reporte RRHH: only ADMIN
    if (f.path === '/reportes_rrhh') {
      const isAdmin = roles && roles.includes(Roles.ADMIN);
      if (!isAdmin) {
        return { ...f, ...{ component: NoAccess } };
      }
    }

    // Formulario 29 / Consolidado F29: moduleId formulario_29 / consolidado_f29 (staff por defecto).

    // Estado de Observación F29: redirige a consolidado_f29; permiso vía consolidado_f29 + rol staff en hub.

    // Cuadre Formulario 29: solo ADMIN, SUPER_COLABORADOR y COLABORADOR
    if (f.path === '/cuadre_formulario_29') {
      const hasCuadreF29Role = roles?.includes(Roles.ADMIN) ||
        roles?.includes(Roles.SUPER_COLABORADOR) ||
        roles?.includes(Roles.COLABORADOR);
      if (!hasCuadreF29Role) return { ...f, ...{ component: NoAccess } };
    }

    // Descargas (Ajustes): staff por defecto; USER_SMARTCFO solo con grant
    // explícito del módulo 'descargas' (resuelto arriba en hasRouteAccess).

    if (!FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))) { //PLANES EN LA LISTA TIENEN ACCESO FULL
      if (companyPlans.includes(Plans.flujo_de_caja) == false) {
        if (['/flujo_caja'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
      }

      if (companyPlans.includes(Plans.estado_de_resultados) == false) {
        if (['/eerr'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
      }

      if (companyPlans.includes(Plans.pago_a_proveedores) == false) {
        if (['/pago_proveedores'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
      }

      // Conciliación: el acceso se controla únicamente desde el panel admin
      // (modulePermissions / hasRouteAccess), ya no por plan de la empresa.
    }

    // Repositorio: acceso por panel admin (hasRouteAccess arriba), sin exigir plan repositorio.

    // if (PLANS_SMARTCFO.some(p => companyPlans.includes(p))) {
    //   if (['/cobranza'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
    // }

    if (roles?.includes('FLUJO_DE_CAJA')) {
      if (['/reportes', '/reportes_dj', '/pago_proveedores', '/eerr'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
    }

    if (roles?.includes('NOMINAS')) {
      if (['/reportes', '/reportes_dj', '/flujo_caja', '/creditos', '/conexiones', '/eerr', '/dashboard'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
    }

    if (roles?.includes('OPERARIO')) {
      if (['/reportes', '/reportes_dj', '/configuracion', '/eerr', '/conexiones'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
    }
    if (roles?.includes('USER_WITH_COBRANZA')) {
      if (['/reportes', '/reportes_dj', '/eerr', '/conexiones','/flujo_caja','/dashboard','/formulario-29','/consolidado-f29','/estado_dj','/estado-observacion','/creditos'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
    }
    if (roles?.includes('USER_WITH_OUT_COBRANZA')) {
      if (['/reportes', '/reportes_dj', '/eerr', '/conexiones','/flujo_caja','/dashboard','/formulario-29','/consolidado-f29','/estado_dj','/estado-observacion','/creditos','/cobranza'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
    }

    if ([].includes(selectedCompany?.id)) { //SOLO PARA CLASSPASS
      if (['/cuadratura'].includes(f.path)) return { ...f, ...{ component: NoAccess } }
    }

    // Contabilidad: hasRouteAccess (arriba) decide por hasAccess; el flag de entorno apaga el build.
    // Ya no se exige rol ADMIN/COLABORADOR/SUPER_COLABORADOR.
    if (ACCOUNTING_ROUTE_PATHS.includes(f.path) && !isAccountingEnabledByEnv()) {
      return { ...f, ...{ component: NoAccess } }
    }

    return f
  })

  return (
          <main className={`${props.maximized ? "c-main-open" : "c-main"}`} style={{ marginLeft: isMobile ? "0px" : (isFixed ? "256px" : "0px"), minHeight: "95vh", display: "flex", flexDirection: "column" }}>
      <CContainer fluid style={{ flex: 1, display: "flex", flexDirection: "column", paddingTop: 15 }}>
        <Suspense fallback={loading}>
          <Switch>
            <Route exact path="/" render={() => <DefaultLandingRedirect />} />
            {Routes.map((route, idx) => {
              return (
                route.component && (
                  <Route
                    key={idx}
                    path={route.path}
                    exact={route.exact}
                    name={route.name}
                    render={(props) => (
                      <CFade style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <CompanyList
                          showCompanyList={showCompanyList}
                          setShowCompanyList={setShowCompanyList}
                          companyName={companyName}
                          hideCompanyList={hideCompanyList}
                          setupdateSideBar={setupdateSideBar}
                        />
                        {route?.path !== "/reportes_personalizados" && route?.pageTitle ? (
                          <h1 className="page-title-header">
                            {location.pathname.startsWith("/repositorio")
                              ? tSidebar("items.repository")
                              : FINANCE_PAGE_TITLE_KEYS[route.path]
                              ? tFinance(`pageTitles.${FINANCE_PAGE_TITLE_KEYS[route.path]}`)
                              : DOCUMENT_PAGE_TITLE_KEYS[route.path]
                                ? tDocuments(`pageTitles.${DOCUMENT_PAGE_TITLE_KEYS[route.path]}`)
                                : SIDEBAR_PAGE_TITLE_KEYS[route.path]
                                  ? tSidebar(`items.${SIDEBAR_PAGE_TITLE_KEYS[route.path]}`)
                                  : PAGE_TITLE_I18N_KEYS[route.path]
                                    ? t(PAGE_TITLE_I18N_KEYS[route.path])
                                    : route.pageTitle}
                          </h1>
                        ) : null}
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                          <ErrorBoundary>
                            <route.component {...props} />
                          </ErrorBoundary>
                        </div>
                      </CFade>
                    )}
                  />
                )
              );
            })}
            <Route render={(props) => <NoAccess {...props} />} />
          </Switch>
        </Suspense>
      </CContainer>
    </main>
  );
};

export default React.memo(Content);
